// Expedition + party wire protocol for Nexus Forge (Phase 5 Step 11).
//
// Shape-only module: types + builders. Same precedent as
// shared/combat-messages.ts and shared/market-messages.ts — the Envelope
// generic is inlined here to avoid a circular import with
// shared/messages.ts (which re-exports the union extension below).
//
// Step 11 ships the PARTY protocol only: invite / accept / decline /
// leave client→server + the four party* server→client durable
// envelopes. Expedition-entry handoff envelopes (expeditionReady,
// expeditionEntered, expeditionCompleted, expeditionExited,
// acceptExpeditionReady, enterExpedition) are reserved for Step 12 —
// they'll land in this same file.
//
// Durability classification (per CLAUDE.md §"Push classification"):
//   DURABLE: partyInvite, partyMemberJoined, partyMemberLeft,
//            partyDissolved — each is a one-shot outcome the client
//            cannot re-derive from a snapshot.
// The push-classification layer (player-party.ts cross-DO callbacks)
// enforces this by routing every outbound envelope through
// PlayerDO.pushDurable.

// -----------------------------------------------------------------------
// Envelope (inlined — same reason as combat-messages.ts)
// -----------------------------------------------------------------------

type Envelope<T extends string, P> = {
  type: T;
  ts: number;
  payload: P;
  seq?: number;
  eventId?: string;
};

// -----------------------------------------------------------------------
// Client → Server
// -----------------------------------------------------------------------

// Leader-only. `targetPlayerId` is the invitee; server rejects self-
// invites, over-size invites, and double-invites with an unexpired TTL.
export type ClientInviteToParty = Envelope<
  "inviteToParty",
  { targetPlayerId: string }
>;

// Invitee-driven. `partyId` echoes the id from the partyInvite envelope
// so a late-accepting invitee whose leader's party changed rejects
// cleanly.
export type ClientAcceptPartyInvite = Envelope<
  "acceptPartyInvite",
  { partyId: string }
>;

export type ClientDeclinePartyInvite = Envelope<
  "declinePartyInvite",
  { partyId: string }
>;

// Empty payload wrapped per CONTRACTS §2.
export type ClientLeaveParty = Envelope<"leaveParty", Record<string, never>>;

// Phase 5 Step 12a — client voluntarily exits an expedition. Empty
// payload per §2; the recipient (ExpeditionDO) knows the expedition
// id + player id from the socket binding.
export type ClientExitExpedition = Envelope<"exitExpedition", Record<string, never>>;

// Phase 5 Step 12b — leader triggers expedition entry. Landed on the
// leader's ZoneDO socket; PlayerDO.enterExpedition validates leader +
// party size, creates the expeditionId, and fans out expeditionReady
// to every party member.
export type ClientEnterExpedition = Envelope<
  "enterExpedition",
  { archetype: string }
>;

// Phase 5 Step 12b — member confirms entry after receiving
// expeditionReady. PlayerDO.acceptExpeditionReady sets
// current_expedition_id; the client then closes the ZoneDO socket and
// reconnects — the Worker routes the new connection to ExpeditionDO.
export type ClientAcceptExpeditionReady = Envelope<
  "acceptExpeditionReady",
  { expeditionId: string }
>;

// Phase 5 Step 17 — symmetric with accept. Clears the invitee's
// pending_expedition_invite row and notifies the ExpeditionDO so
// Phase 6 decline-heuristics have a hook point. Idempotent: declining
// a non-pending id is a no-op.
export type ClientDeclineExpeditionReady = Envelope<
  "declineExpeditionReady",
  { expeditionId: string }
>;

export type ClientPartyMessage =
  | ClientInviteToParty
  | ClientAcceptPartyInvite
  | ClientDeclinePartyInvite
  | ClientLeaveParty
  | ClientExitExpedition
  | ClientEnterExpedition
  | ClientAcceptExpeditionReady
  | ClientDeclineExpeditionReady;

// -----------------------------------------------------------------------
// Server → Client
// -----------------------------------------------------------------------

// DURABLE. Delivered to invitee via cross-DO pushDurable (replay-
// buffer delivers even if offline). `invitedAt` + `ttlMs` let the
// UI render a countdown without reading balance.
export type ServerPartyInvite = Envelope<
  "partyInvite",
  { partyId: string; leaderId: string; leaderName: string; invitedAt: number; ttlMs: number }
>;

// DURABLE. Leader → every party member (incl. new member) after
// acceptPartyInvite. `leaderId` (Step 21 followup) seeds
// roster.leaderId for non-inviter members without a placeholder.
export type ServerPartyMemberJoined = Envelope<
  "partyMemberJoined",
  { partyId: string; leaderId: string; memberId: string; memberName: string }
>;

// DURABLE. Broadcast from the leader to remaining party members when a
// non-leader leaves or declines (post-join). The leaving member
// receives their own partyMemberLeft as a self-confirmation.
export type ServerPartyMemberLeft = Envelope<
  "partyMemberLeft",
  { partyId: string; memberId: string }
>;

// DURABLE. Delivered to every member (including the leader) when the
// leader leaves or the party otherwise dissolves. The leader's own
// PlayerDO clears local state before dispatching; members clear on
// receipt via their own callback.
export type ServerPartyDissolved = Envelope<
  "partyDissolved",
  { partyId: string }
>;

// Phase 5 Step 12b server→client envelopes (all DURABLE).

// Broadcast to every party member when the leader triggers entry.
// Client shows the accept/decline modal; acceptExpeditionReady is the
// reply. `archetype` drives the modal's copy + art.
export type ServerExpeditionReady = Envelope<
  "expeditionReady",
  { expeditionId: string; archetype: string; leaderId: string }
>;

// Personal confirmation to a member after their current_expedition_id
// has been set — client takes this as the cue to close the ZoneDO
// socket and reconnect via /ws so the Worker routes to ExpeditionDO.
export type ServerExpeditionEntered = Envelope<
  "expeditionEntered",
  { expeditionId: string }
>;

// Phase 5 Step 21 followup — EPHEMERAL "go reconnect" cue pushed on
// the ZoneDO socket right after acceptExpeditionReady flips
// current_expedition_id; `expeditionEntered` durable is already
// routing to ExpeditionDO by then. Missed cue = resume-replay path.
export type ServerExpeditionReadyConfirmed = Envelope<
  "expeditionReadyConfirmed",
  { expeditionId: string }
>;

// Personal confirmation after ExpeditionDO.playerExits completes. Paired
// with the socket close; the client cleans up expedition-scoped state.
export type ServerExpeditionExited = Envelope<
  "expeditionExited",
  { expeditionId: string }
>;

// Phase 5 Step 13b — personal delivery of rolled loot. Pushed from
// PlayerDO.deliverLoot after the idempotency check + addItem writes.
// `source` carries the mob type so clients can animate a kill/box
// differently; `items` is the post-roll quantity, not the table entry
// list (clients don't re-roll).
export type ServerLootRolled = Envelope<
  "lootRolled",
  {
    lootRollId: string;
    items: Array<{ itemId: string; quantity: number }>;
    source: { type: "mob" | "boss"; mobType: string };
  }
>;

// Phase 5 Step 13b — broadcast to every alive member on boss defeat or
// abandonment. `reason` is stable machine-readable text so the client
// can pick the right exit flow ('boss_defeated' = completion modal,
// 'abandoned' = notice + reconnect to ZoneDO). Step 14 wires the
// 'abandoned' branch.
export type ServerExpeditionCompleted = Envelope<
  "expeditionCompleted",
  { expeditionId: string; reason: "boss_defeated" | "abandoned" }
>;

// Phase 5 Step 16a — EPHEMERAL. Fired once per combat tick carrying
// the current positions of every alive member + alive mob inside the
// expedition. No seq, no eventId; clients re-derive peer + mob world
// positions from this stream so party sprites and mob sprites stay
// visually current without burning replay-buffer rows. A dropped
// envelope is harmless — the next tick supersedes it.
export type ServerExpeditionPositions = Envelope<
  "expeditionPositions",
  {
    members: Array<{ playerId: string; x: number; y: number }>;
    mobs: Array<{ mobId: string; x: number; y: number }>;
  }
>;

export type ServerPartyMessage =
  | ServerPartyInvite
  | ServerPartyMemberJoined
  | ServerPartyMemberLeft
  | ServerPartyDissolved
  | ServerExpeditionReady
  | ServerExpeditionEntered
  | ServerExpeditionReadyConfirmed
  | ServerExpeditionExited
  | ServerLootRolled
  | ServerExpeditionCompleted
  | ServerExpeditionPositions;

// -----------------------------------------------------------------------
// Builders — one per message type. Names match the contracts §2 regex
// /^(build|make|create).*Message$/.
// -----------------------------------------------------------------------

export const buildInviteToPartyMessage = (
  targetPlayerId: string,
): ClientInviteToParty => ({
  type: "inviteToParty",
  ts: Date.now(),
  payload: { targetPlayerId },
});

export const buildAcceptPartyInviteMessage = (
  partyId: string,
): ClientAcceptPartyInvite => ({
  type: "acceptPartyInvite",
  ts: Date.now(),
  payload: { partyId },
});

export const buildDeclinePartyInviteMessage = (
  partyId: string,
): ClientDeclinePartyInvite => ({
  type: "declinePartyInvite",
  ts: Date.now(),
  payload: { partyId },
});

export const buildLeavePartyMessage = (): ClientLeaveParty => ({
  type: "leaveParty",
  ts: Date.now(),
  payload: {},
});

export const buildExitExpeditionMessage = (): ClientExitExpedition => ({
  type: "exitExpedition",
  ts: Date.now(),
  payload: {},
});

export const buildEnterExpeditionMessage = (
  archetype: string,
): ClientEnterExpedition => ({
  type: "enterExpedition",
  ts: Date.now(),
  payload: { archetype },
});

export const buildAcceptExpeditionReadyMessage = (
  expeditionId: string,
): ClientAcceptExpeditionReady => ({
  type: "acceptExpeditionReady",
  ts: Date.now(),
  payload: { expeditionId },
});

export const buildDeclineExpeditionReadyMessage = (
  expeditionId: string,
): ClientDeclineExpeditionReady => ({
  type: "declineExpeditionReady",
  ts: Date.now(),
  payload: { expeditionId },
});

export const buildExpeditionReadyMessage = (
  expeditionId: string,
  archetype: string,
  leaderId: string,
): ServerExpeditionReady => ({
  type: "expeditionReady",
  ts: Date.now(),
  payload: { expeditionId, archetype, leaderId },
});

export const buildExpeditionEnteredMessage = (
  expeditionId: string,
): ServerExpeditionEntered => ({
  type: "expeditionEntered",
  ts: Date.now(),
  payload: { expeditionId },
});

export const buildExpeditionReadyConfirmedMessage = (
  expeditionId: string,
): ServerExpeditionReadyConfirmed => ({
  type: "expeditionReadyConfirmed",
  ts: Date.now(),
  payload: { expeditionId },
});

export const buildExpeditionExitedMessage = (
  expeditionId: string,
): ServerExpeditionExited => ({
  type: "expeditionExited",
  ts: Date.now(),
  payload: { expeditionId },
});

export const buildLootRolledMessage = (
  lootRollId: string,
  items: Array<{ itemId: string; quantity: number }>,
  source: { type: "mob" | "boss"; mobType: string },
): ServerLootRolled => ({
  type: "lootRolled",
  ts: Date.now(),
  payload: { lootRollId, items, source },
});

export const buildExpeditionCompletedMessage = (
  expeditionId: string,
  reason: "boss_defeated" | "abandoned",
): ServerExpeditionCompleted => ({
  type: "expeditionCompleted",
  ts: Date.now(),
  payload: { expeditionId, reason },
});

export const buildExpeditionPositionsMessage = (
  members: Array<{ playerId: string; x: number; y: number }>,
  mobs: Array<{ mobId: string; x: number; y: number }>,
): ServerExpeditionPositions => ({
  type: "expeditionPositions",
  ts: Date.now(),
  payload: { members, mobs },
});

export const buildPartyInviteMessage = (
  partyId: string,
  leaderId: string,
  leaderName: string,
  invitedAt: number,
  ttlMs: number,
): ServerPartyInvite => ({
  type: "partyInvite",
  ts: Date.now(),
  payload: { partyId, leaderId, leaderName, invitedAt, ttlMs },
});

export const buildPartyMemberJoinedMessage = (
  partyId: string,
  leaderId: string,
  memberId: string,
  memberName: string,
): ServerPartyMemberJoined => ({
  type: "partyMemberJoined",
  ts: Date.now(),
  payload: { partyId, leaderId, memberId, memberName },
});

export const buildPartyMemberLeftMessage = (
  partyId: string,
  memberId: string,
): ServerPartyMemberLeft => ({
  type: "partyMemberLeft",
  ts: Date.now(),
  payload: { partyId, memberId },
});

export const buildPartyDissolvedMessage = (
  partyId: string,
): ServerPartyDissolved => ({
  type: "partyDissolved",
  ts: Date.now(),
  payload: { partyId },
});
